import React, {
  useState,
  useEffect,
  useRef,
  ChangeEvent,
  DragEvent,
  DragEventHandler,
} from 'react';
import Uppy from '@uppy/core';
import AwsS3Multipart from '@uppy/aws-s3-multipart';
import { UppyFile } from '@uppy/core'; // Import UppyFile type
import { MediaKitSettingModel } from './KitModels';
import { showToast } from '../../shared/Toast';
import Button from '../../submission_boards/component/Button';
import InlineButton from '../../submission_boards/component/InlineButton';
import {
  CircleCheckBigIcon,
  CircleMinusIcon,
  CircleXIcon,
  CrossIcon,
  FileXIcon,
  InfoIcon,
  UploadIcon,
  XIcon,
} from 'lucide-react';
import FilePreviewer from './FilePreviewer';
import { KitDocumentType } from '../../submission_boards/component/SbModel';

interface CustomUppyUploaderProps {
  isReplaced: boolean;
  backendUrl: string; // Base URL for your backend S3 pre-signing endpoint
  mediaSetting: MediaKitSettingModel;
  mediaType?: number;
  maxFileSizeMb?: number;
  maxUploadFiles: number;
  handleCloseUploader: () => void;
  filesUploaded: (file: any) => void;
  uploadCompleted?: () => void;
  handleErrorMessage: (error: string) => void;
  validateSameFileName?: (uppyFiles: any) => string[];
}

interface UploadedFileState {
  progress: number; // 0-100
  status: 'pending' | 'uploading' | 'success' | 'error' | 'canceled';
  uploadURL?: string; // S3 URL after successful upload
  errorMessage?: string;

  file?: UppyFile; // The actual File object
  previewUrl: string; // The URL created by URL.createObjectURL
}

const CustomUppyUploaderv2: React.FC<CustomUppyUploaderProps> = ({
  isReplaced,
  backendUrl,
  mediaSetting,
  mediaType,
  maxFileSizeMb,
  maxUploadFiles,
  handleCloseUploader,
  filesUploaded,
  uploadCompleted,
  handleErrorMessage,
  validateSameFileName,
}) => {
  const uppy = useRef<Uppy | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [mediaSettingSelected, setMediaSettingSelected] = useState<MediaKitSettingModel>(mediaSetting);
  const version: number = 2;

  // for testing changed it
  enum UploadPanels {
    SelectFiles = 1,
    ShowFiles = 2,
    UploadComplete = 3,
    UploadError = 4,
  }
  const [showUploadPanel, setShowUploadPanel] = useState<number>(UploadPanels.SelectFiles);
  // const [uploadComplete, setUploadComplete] = useState<boolean>(false);

  const [uploadedFileStates, setUploadedFileStates] = useState<UploadedFileState[]>([]);
  const [totalProgress, setTotalProgress] = useState<number>(0); // Overall upload progress
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [acceptType, setAcceptType] = useState<string>('');
  // 'image/*,video/*,.pdf,.doc,.docx';
  useEffect(() => {
    if (!uppy.current) {
      let allowedTypes = [];
      if (maxFileSizeMb == undefined || maxFileSizeMb <= 0) {
        maxFileSizeMb = 30;
      }
      if (mediaSettingSelected.imageCount >= 0) {
        if (mediaSettingSelected.imageAllowed > 0 && (mediaType == undefined || mediaType == 1)) {
          allowedTypes.push('image/*');
          // Image (30MB):
          // JPG
          // PNG
          // GIF
          // HEIC
          // WebP
          setAcceptType('image/jpeg, image/png, image/gif, image/heic, image/webp');
        } else if (
          mediaSettingSelected.videoAllowed > 0 &&
          (mediaType == undefined || mediaType == 2)
        ) {
          allowedTypes.push('video/*');
          // Video (500MB):
          // MOV
          // MP4
          // WebM
          setAcceptType('video/mp4, video/quicktime, video/webm');
        } else if (
          mediaSettingSelected.videoAllowed > 0 ||
          mediaSettingSelected.imageAllowed > 0 ||
          mediaType == undefined ||
          mediaType == 3
        ) {
          allowedTypes.push('image/*');
          allowedTypes.push('video/*');
          // Video (500MB):
          // MOV
          // MP4
          // WebM
          setAcceptType(
            'image/jpeg, image/png, image/gif, image/heic, image/webp, video/mp4, video/quicktime, video/webm'
          );
        }
      }
      uppy.current = new Uppy({
        debug: false, // Set to false in production. syca_check
        autoProceed: false, // We'll manually trigger upload
        restrictions: {
          maxNumberOfFiles: maxUploadFiles,
          maxFileSize: maxFileSizeMb * 1024 * 1024, // 100MB (adjust as needed)
          allowedFileTypes: allowedTypes,
        },
      });

      // Add the AWS S3 Multipart plugin
      uppy.current.use(AwsS3Multipart, {
        companionUrl: '/', // Uppy will append /s3/multipart or /s3/params
        // serverUrl: backendUrl, // Ensure this matches Companion's base URL if you're using it
        // get-url: `${backendUrl}/s3-multipart`, // Specify your custom endpoint for multipart signing if not using companion's default routes.
        // It's crucial that this backend endpoint correctly implements the S3 multipart protocol.
        // If you are using `@uppy/aws-s3` (single-part), the endpoint would be simpler like `${backendUrl}/s3-sign-url`.
      });

      // --- Uppy Event Listeners ---
      uppy.current.on('file-added', (file: UppyFile) => {
        // Add file to our state for display
        uppy_file_added(file);
        // setUploadComplete(false); // Reset completion status on new files
      });

      uppy.current.on('file-removed', (file: UppyFile) => {
        setUploadedFileStates((prevFiles) => {
          const fileToRemove = prevFiles.find((f) => f.file.id === file.id);
          if (fileToRemove?.previewUrl) {
            URL.revokeObjectURL(fileToRemove.previewUrl); // Clean up the Blob URL
          }
          return prevFiles.filter((f) => f.file.id !== file.id);
        });
      });

      uppy.current.on(
        'upload-progress',
        (file: UppyFile, progress: { bytesUploaded: number; bytesTotal: number }) => {
          // Update individual file's progress
          setUploadedFileStates((prevFiles) =>
            prevFiles.map((f) =>
              f.file.id === file.id
                ? {
                    ...f,
                    progress: Math.round((progress.bytesUploaded / progress.bytesTotal) * 100),
                    status: 'uploading',
                  }
                : f
            )
          );
        }
      );

      uppy.current.on('progress', (totalProgress: number) => {
        // Update overall progress
        setTotalProgress(totalProgress);
      });

      uppy.current.on('upload-success', async (file: UppyFile, response: any) => {
        console.log('uppy.current.upload-success');
        // try {
        //   // Mark file as success and store its URL
        //   uppy.current.setFileState(file.id, {
        //     isPostProcessing: true,
        //     progress: { percentage: 90, uploadComplete: false },
        //   });
        //   await filesUploaded(file);
        //   setUploadedFileStates((prevFiles) =>
        //     prevFiles.map((f) =>
        //       f.file.id === file.id
        //         ? { ...f, status: 'success', uploadURL: response.uploadURL || response.body.url }
        //         : f
        //     )
        //   );
        // } catch (error) {
        //   uppy.current.setFileState(file.id, {
        //     error: error.message,
        //     isPostProcessing: false, // Must be set to false to allow completion check
        //     progress: { uploadComplete: true },
        //   });
        //   setUploadedFileStates((prevFiles) =>
        //     prevFiles.map((f) =>
        //       f.file.id === file.id
        //         ? { ...f, status: 'error', errorMessage: 'Failed to upload.' }
        //         : f
        //     )
        //   );
        // }
        console.log('uppy.current.upload-success end');
      });

      uppy.current.on('complete', async (result) => {
        console.log('uppy.current.complete');
        try{
        for (
          let indexsuccessful = 0;
          indexsuccessful < result.successful.length;
          indexsuccessful++
        ) {
          let file = result.successful[indexsuccessful];
          try {
            await filesUploaded(file);
            const uploadURL: string = file.response?.uploadURL ?? '';
            setUploadedFileStates((prevFiles) =>
              prevFiles.map((f) =>
                f.file.id === file.id
                  ? {
                      ...f,
                      status: 'success',
                      uploadURL: uploadURL,
                    }
                  : f
              )
            );
          } catch (error) {
            uppy.current.setFileState(file.id, {
              error: error.message,
              isPostProcessing: false, // Must be set to false to allow completion check
              progress: { uploadComplete: true },
            });
            result.failed.push({ ...file, ...{ error: 'Failed to upload.' } });
            setUploadedFileStates((prevFiles) =>
              prevFiles.map((f) =>
                f.file.id === file.id
                  ? { ...f, status: 'error', errorMessage: 'Failed to upload.' }
                  : f
              )
            );
          }
        }
        if (result.failed.length === 0) {
          setShowUploadPanel(UploadPanels.UploadComplete);
          // setUploadComplete(true); // All files uploaded successfully
          console.log('Upload successful!', result);
          if (uploadCompleted) {
            await uploadCompleted();
          }
        } else {
          setShowUploadPanel(UploadPanels.UploadError);
          setErrorMessage('Error in uploading media. Please try again later.');
          // handleErrorMessage(
          //   'Error in uploading media. Please try again later.'
          // );
          setUploadedFileStates((prevFiles) =>
            prevFiles.map((f) => {
              if (result.failed.some((failedFile) => failedFile.id === f.file.id)) {
                return { ...f, status: 'error', errorMessage: 'Upload failed' }; // Basic error message
              }
              return f;
            })
          );
        }
        } catch(e) {
          console.log(e);
        }
        finally{
          setIsUploading(false); // Upload process finished
        }
      });

      uppy.current.on('upload-error', async (file: UppyFile, response: any) => {
        console.log('uppy.current.error', file);
        setUploadedFileStates((prevFiles) =>
          prevFiles.map((f) =>
            f.file.id === file.id ? { ...f, status: 'error', errorMessage: 'Upload failed' } : f
          )
        );
        console.log('uppy.current.error end');
      });
      uppy.current.on('error', (error) => {
        console.error('Uppy error:', error);
        setIsUploading(false);
        // alert(`Uppy Error: ${error.message}`);
      });
    }

    // Cleanup Uppy instance on component unmount
    return () => {
      uploadedFileStates.forEach((file) => {
        // Make sure to clean up any remaining preview URLs
        console.log('cleanup previewurl');
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      if (uppy.current) {
        console.log('cleanup uppy.current');
        uppy.current.close();
        uppy.current = null; // Important to prevent memory leaks
      }
    };
  }, []); // Re-initialize if backendUrl changes (unlikely for a single app)

  useEffect(() => {
    setMediaSettingSelected(mediaSetting);
  }, [mediaSetting]); // Re-initialize if backendUrl changes (unlikely for a single app)

  useEffect(() => {
    if (uppy.current) {
      let allowedTypes = [];
      if (maxFileSizeMb == undefined || maxFileSizeMb <= 0) {
        maxFileSizeMb = 5;
      }
      if (mediaSettingSelected.imageAllowed > 0 && (mediaType == undefined || mediaType == 1)) {
        allowedTypes.push('image/*');
        // Image (30MB):
        // JPG
        // PNG
        // GIF
        // HEIC
        // WebP
        setAcceptType('image/jpeg, image/png, image/gif, image/heic, image/webp');
      } else if (
        mediaSettingSelected.videoAllowed > 0 &&
        (mediaType == undefined || mediaType == 2)
      ) {
        allowedTypes.push('video/*');
        // Video (500MB):
        // MOV
        // MP4
        // WebM
        setAcceptType('video/mp4, video/quicktime, video/webm');
      } else if (
        mediaSettingSelected.videoAllowed > 0 ||
        mediaSettingSelected.imageAllowed > 0 ||
        mediaType == undefined ||
        mediaType == 3
      ) {
        setAcceptType(
          'image/jpeg, image/png, image/gif, image/heic, image/webp, video/mp4, video/quicktime, video/webm'
        );
      }

      uppy.current.setOptions({
        restrictions: {
          allowedFileTypes: allowedTypes,
        },
      });
    }

    // Cleanup Uppy instance on component unmount
    return () => {
      uploadedFileStates.forEach((file) => {
        // Make sure to clean up any remaining preview URLs
        console.log('cleanup previewurl');
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
    };
  }, [mediaSettingSelected]); // Re-initialize if backendUrl changes (unlikely for a single app)

  useEffect(() => {
    console.log('uploadedFileStates', uploadedFileStates);
    if (uploadedFileStates.length <= 0) {
      setTotalProgress(0);
      setIsUploading(false);
      // setUploadComplete(false);
      setShowUploadPanel(UploadPanels.SelectFiles);
    }
  }, [uploadedFileStates]); // Re-initialize if backendUrl changes (unlikely for a single app)

  const uppy_file_added = (file:UppyFile) =>{
    let previewUrl = '';
    console.log(
      'CustomUppyUploaderv2:useEffect:uppy_file_added mediaSettingSelected',
      mediaSettingSelected
    );
    if (file.data && (file.type?.startsWith('image/') || file.type?.startsWith('video/'))) {
      previewUrl = URL.createObjectURL(file.data);
    }
    if (file.type?.startsWith('image/')) {
      const resp = uploadedFileStates.filter((fileItem) => {
        return fileItem.file.type?.startsWith('image/');
      });
      if (resp && resp.length + 1 > mediaSettingSelected.imageAllowed) {
        return;
      }
    }
    if (file.type?.startsWith('video/')) {
      const resp = uploadedFileStates.filter((fileItem) => {
        return fileItem.file.type?.startsWith('cideo/');
      });
      if (resp && resp.length + 1 > mediaSettingSelected.videoAllowed) {
        return;
      }
    }
    setUploadedFileStates((prevFiles) => [
      ...prevFiles,
      {
        previewUrl: previewUrl,
        file: file,
        progress: 0,
        status: 'pending',
      },
    ]);
  }
  const handleUpload = async () => {
    if (version == 2) {
      if (uppy.current && uploadedFileStates.length > 0) {
        setIsUploading(true);
        try {
          setErrorMessage('');
          // setUploadComplete(false);
          await uppy.current.upload(); // Start the upload process
        } catch (error) {
          setErrorMessage(
            'Error in uploading media. Please check your internet or try again later.'
          );
          console.log('Error in uploading, check internet connection', error);
        }
      } else {
        // syca_check alert('Please select files to upload.');
      }
    } else {
      showToast('WIP', 'danger');
    }
  };

  const handleClearFiles = () => {
    if (uppy.current) {
      uppy.current.getFiles().forEach((file) => {
        uppy.current?.removeFile(file.id);
      });
      setUploadedFileStates([]);
      setTotalProgress(0);
      setIsUploading(false);
      setErrorMessage('');
      // setUploadComplete(false);
      setShowUploadPanel(UploadPanels.SelectFiles);
    }
  };
  const handleClose = () => {
    handleClearFiles();
    handleCloseUploader();
    setShowUploadPanel(UploadPanels.SelectFiles);
  };
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // This is crucial to allow the onDrop event to fire
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    setIsDraggingOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
    setErrorMessage('');
    const droppedFiles = Array.from(e.dataTransfer.files);
    console.log('Dropped files:', droppedFiles);
    addInUppyList(droppedFiles);
  };

  const addInUppyList = (droppedFiles: any) => {
    let currentImage = 1;
    let currentVideo = 1;
    let reachMaxed = false;
    let showNextPanel = false;
    Array.from(droppedFiles).forEach((file: any) => {
      try {
        const present = uppy.current.getFiles().find((fileAdded) => {
          return fileAdded.name == file.name;
        });
        if (present) {
          // setErrorMessage(file.name + ' file is already selected.');
          return;
        }
        if (file.type?.startsWith('image/')) {
          const resp = uploadedFileStates.filter((fileItem) => {
            return fileItem.file.type?.startsWith('image/');
          });
          if (
            currentImage > mediaSettingSelected.imageAllowed ||
            (resp && resp.length + currentImage > mediaSettingSelected.imageAllowed)
          ) {
            reachMaxed = true;
            return;
          }
          currentImage++;
        }
        if (file.type?.startsWith('video/')) {
          const resp = uploadedFileStates.filter((fileItem) => {
            return fileItem.file.type?.startsWith('cideo/');
          });
          if (resp && resp.length + currentVideo > mediaSettingSelected.videoAllowed) {
            reachMaxed = true;
            return;
          }
          currentVideo++;
        }
        let filePresents = [];
        if (validateSameFileName) {
          filePresents = validateSameFileName([file]);
          if (filePresents.length > 0) {
            setErrorMessage(filePresents.join(', ') + ' files already selected.');
          }
        }
        if (filePresents.length <= 0) {
          showNextPanel = true;
          uppy.current?.addFile({
            data: file,
            name: file.name,
            type: file.type,
            size: file.size,
          });
        }

        // setShowUploadPanel(UploadPanels.UploadError); // syca_check
      } catch (error) {
        setErrorMessage(error.message);
        console.log('Error in uploading, check internet connection', error);
      }
    });
    if (showNextPanel || uploadedFileStates.length > 0) {
      setShowUploadPanel(UploadPanels.ShowFiles);
    }
    if (reachMaxed && !isReplaced) {
      setErrorMessage('Upload limit reached.');
      showToast('Upload limit reached.', 'danger');
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && uppy.current) {
      setErrorMessage('');
      addInUppyList(event.target.files);

      // Array.from(event.target.files).forEach((file) => {
      //   uppy.current?.addFile({
      //     data: file,
      //     name: file.name,
      //     type: file.type,
      //     size: file.size,
      //   });
      // });
      event.target.value = '';
    }
  };
  return (
    <div
      className={
        'rounded-lg flex flex-col px-4 pt-10 pb-5 gap-2-1  m-4 ' +
        (showUploadPanel === UploadPanels.UploadError
          ? 'bg-red-702 border-2 border-dashed border-red-500'
          : showUploadPanel === UploadPanels.UploadComplete
          ? 'bg-green-50'
          : 'bg-blue-50 border-2 border-dashed border-blue-500')
      }
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
    >
      <div className="w-full mx-auto">
        <div className="w-full  mx-auto ">
          {showUploadPanel == UploadPanels.SelectFiles && (
            <>
              <div
                className="flex flex-col items-center justify-center"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center justify-center ">
                  <div className="font-medium text-lg text-center">
                    Upload {mediaType == 1 ? 'image' : mediaType == 2 ? 'video' : 'image/video'}{' '}
                    files or drag and drop
                  </div>
                  <div className="text-base font-normal text-gray-500 mb-5 text-center">
                    Upload {maxUploadFiles <= 1 ?' file':'up to  5 files at a time'}. Max file size allowed: {maxFileSizeMb >= 1024?(maxFileSizeMb/1024) + ' GB': maxFileSizeMb + ' MB' }
                  </div>
                </div>
                <div className="flex flex-col items-center justify-center ">
                  <div className="font-normal text-sm text-gray-400">
                    {mediaType == 1
                      ? 'JPEG, PNG image files'
                      : mediaType == 2
                      ? 'MP4, MOV, AVI video files.'
                      : 'JPEG, PNG, MP4, MOV, AVIfiles.'}
                  </div>
                  <div className="font-normal text-sm text-gray-400 text-center">
                    Note: When uploading a large file, it may take up to a few minutes for the
                    thumbnail to be generated.
                  </div>
                </div>
                  { !isReplaced && ((mediaType == KitDocumentType.Image && mediaSettingSelected.imageAllowed > 0) ||
                (mediaType == KitDocumentType.Video && mediaSettingSelected.videoAllowed > 0)) && (
                <div className="flex flex-col sm:flex-row items-center justify-center px-2-1 py-2-1 mt-4 gap-2 text-center">
                  <InfoIcon className="w-4 h-4 opacity-100 text-blue-600" strokeWidth={1.5} />
                  <div className="font-normal text-base text-blue-600 ">
                    {'You can upload ' +
                      (mediaType == KitDocumentType.Image
                        ? mediaSettingSelected.imageAllowed
                        : mediaSettingSelected.videoAllowed) +
                      ' more ' + (mediaType == KitDocumentType.Image?'images':'videos' )}
                  </div>
                </div>
              )}
              </div>
              {errorMessage.length > 0 && (
                <div className="flex flex-row mt-4 items-center justify-center">
                  <div className="flex flex-row px-2-1 py-2-1 gap-2 rounded-md bg-red-701 border-red-450 border text-red-601 text-center break-all">
                    <CircleXIcon className="w-6 h-6" />
                    <div className="font-medium font-xs">{errorMessage}</div>
                  </div>
                </div>)}
              <div className="fflex flex-row justify-center items-center gap-6 py-2.5 sm:py-10">
                <Button
                  className="w-full flex-1 "
                  onClick={() => {
                    // setUploadComplete(false);
                    fileInputRef.current?.click();
                  }}
                >
                  {maxUploadFiles <= 1 ?'Select File':'Select Files'}
                </Button>
              </div>
            </>
          )}
          {/* Hidden file input */}
          <input
            type="file"
            multiple={maxUploadFiles <= 1 ? false : true}
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept={acceptType} // Use the same accept as Uppy restrictions for consistency
          />

          {/* Display selected files */}
          {(showUploadPanel == UploadPanels.ShowFiles ||
            showUploadPanel == UploadPanels.UploadError) && (
            <div className="flex flex-col justify-center w-full">
              <div className="flex flex-row justify-center w-full">
                {showUploadPanel == UploadPanels.ShowFiles && (
                  <div className="text-lg font-semibold">
                    {isUploading ? 'Selected Files:' : 'Selected Files To Be Uploaded:'}
                  </div>
                )}
                {showUploadPanel == UploadPanels.UploadError && (
                  <div className="text-lg font-semibold">
                    Some files were uploaded successfully, but one or more failed:
                  </div>
                )}
              </div>
              <div className="gap-2-1 px-2-1 py-2-1 flex flex-row flex-wrap justify-center items-start content-center  ">
                {uploadedFileStates.map((uploadedFileState, index) => (
                  <div
                    key={uploadedFileState.file.id}
                    className="box-shadow-102 flex flex-col items-center p-4 gap-4 rounded-lg bg-white w-44"
                  >
                    <div className="flex-grow flex w-full items-start justify-center bg-gray-100 rounded-lg overflow-hidden">
                      <div className="w-full max-h-48 ">
                        <FilePreviewer
                          showControl={false}
                          file_url={uploadedFileState.previewUrl}
                          file_name={uploadedFileState.file.name}
                          file_content_type={uploadedFileState.file.type}
                        />
                      </div>
                      {/* {uploadedFileState.file.type.startsWith('image') ? (
                      <img
                        draggable={false}
                        src={uploadedFileState.previewUrl}
                        alt={uploadedFileState.file.name}
                        className="max-w-full max-h-48 object-contain rounded-md"
                      />
                    ) : uploadedFileState.file.type.startsWith('video') ? (
                      <video
                        draggable={false}
                        src={uploadedFileState.previewUrl}
                        controls
                        className="max-w-full max-h-48 object-contain rounded-md"
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-12 w-12 mx-auto text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0014.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="mt-2 text-sm">{uploadedFileState.file.name}</p>
                        <p className="text-xs text-gray-400">({uploadedFileState.file.type})</p>
                      </div>
                    )} */}
                    </div>
                    <div className="flex flex-col justify-center w-full items-center">
                      <div className="py-0.5 w-full flex items-start justify-center truncate">
                        <div className="font-medium text-sm overflow-hidden">
                          {uploadedFileState.file.name}
                        </div>
                      </div>

                      <div className="font-medium text-xs text-gray-500 py-0.5">
                        {(uploadedFileState.file.data.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                      <div className="font-medium text-xs py-0.5">
                        {uploadedFileState.status === 'pending' && (
                          <>
                            <span className="text-gray-500">Pending</span>
                            <span className="text-gray-500">
                              {' / '}({uploadedFileState.progress}%)
                            </span>
                          </>
                        )}
                        {uploadedFileState.status === 'uploading' && (
                          <>
                            <span className="text-blue-500">{uploadedFileState.progress === 100 ? 'Processing...' : 'Uploading...'}</span>
                            <span className="text-gray-500">
                              {' / '}({uploadedFileState.progress}%)
                            </span>
                          </>
                        )}
                        {uploadedFileState.status === 'success' && (
                          <div className="text-green-600 flex flex-col items-center">
                            <div className="text-gray-500 flex flex-col items-center">
                              File Uploaded!
                            </div>
                            <CircleCheckBigIcon className="w-8 h-8" />
                            {/* {uploadedFileState.uploadURL && (
                            <a
                              href={uploadedFileState.uploadURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline ml-1"
                            >
                              View
                            </a>
                          )} */}
                          </div>
                        )}
                        {uploadedFileState.status === 'error' && (
                          <div className="text-red-500 flex flex-col items-center">
                            <div className="text-gray-500 flex flex-col items-center">
                              Upload has failed{' '}
                              {/*  : {uploadedFileState.errorMessage || 'Unknown error'} */}
                            </div>
                            <FileXIcon className="w-8 h-8" />
                          </div>
                        )}
                        {uploadedFileState.status === 'canceled' && (
                          <span className="text-orange-500">Canceled</span>
                        )}
                      </div>
                      {!isUploading && uploadedFileState.status !== 'success' && (
                        <div className="font-medium text-xs pt-2">
                          <InlineButton
                            id={'remove' + index}
                            variant="secondary"
                            onClick={() => {
                              console.log('remove file');
                              uppy.current?.removeFile(uploadedFileState.file.id);
                              setErrorMessage('');
                            }} // Use Uppy's removeFile, which triggers our listener
                            className="gap-2-1 text-red-600 hover:text-red-800 text-sm font-semibold focus:outline-none"
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              <XIcon className="w-4 h-4" />
                            </div>
                            Remove
                          </InlineButton>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Action Buttons */}
          {showUploadPanel == UploadPanels.ShowFiles && !isUploading && (
            <>
              {errorMessage.length > 0 && (
                <div className="flex flex-row mt-4 items-center justify-center">
                  <div className="flex flex-row px-2-1 py-2-1 gap-2 rounded-md bg-red-701 border-red-450 border text-red-601">
                    <CircleXIcon className="w-6 h-6" />
                    <div className="font-medium font-xs">{errorMessage}</div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2-1 items-center justify-center sm:space-x-4 mt-6 px-4 sm:px-10 py-10">
                <Button
                  variant="secondary"
                  className="flex-1 w-full sm:w-auto"
                  onClick={() => {
                    if (uploadedFileStates.length >= 5) {
                      setErrorMessage('You can only upload 5 files');
                    } else {
                      setShowUploadPanel(UploadPanels.SelectFiles);
                    }
                  }}
                  disabled={!uploadedFileStates.length || isUploading}
                >
                  {maxUploadFiles <= 1 ?'Select File':'Select Files'}
                </Button>

                <Button
                  variant="secondary"
                  className="flex-1 w-full sm:w-auto"
                  onClick={handleClearFiles}
                  disabled={!uploadedFileStates.length || isUploading}
                >
                  <div className="w-6 h-6  flex items-center justify-center">
                    <CircleMinusIcon className="w-4-2 h-4-2" />
                  </div>
                  Clear Files
                </Button>
                <Button
                  variant="primary"
                  className="flex-1 w-full sm:w-auto"
                  onClick={handleUpload}
                  disabled={!uploadedFileStates.length || isUploading}
                >
                  <div className="w-6 h-6  flex items-center justify-center">
                    <UploadIcon className="w-4-2 h-4-2" />
                  </div>
                  {isUploading ? (totalProgress === 100 ? 'Processing...' : 'Uploading...') : 'Start Upload'}
                </Button>
              </div>
            </>
          )}

          {showUploadPanel == UploadPanels.UploadError && (
            <>
              <div className="flex space-x-4 pt-8 pb-8">
                <div className="flex-1">
                  <div className="flex flex-row px-2-1 py-2-1 mt-4 gap-2 relative rounded-md bg-red-701 border-red-450 border">
                    <CircleXIcon className="w-6 h-6" />
                    <div className="font-medium font-xs">
                      Upload completed with errors: some files could not be uploaded.
                    </div>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    handleClearFiles();
                    handleCloseUploader();
                  }}
                >
                  {'Close'}
                </Button>
              </div>
            </>
          )}
          {/* Overall Progress Bar */}
          {(showUploadPanel == UploadPanels.ShowFiles ||
            showUploadPanel == UploadPanels.UploadError) &&
            uploadedFileStates.length > 0 &&
            // !uploadComplete &&
            (totalProgress > 0 || isUploading) && (
              <div className="flex flex-col justify-center w-full py-10 px-4 sm:px-10">
                <div className="flex flex-row justify-center w-full pb-5">
                  <div className="text-lg font-semibold">{totalProgress === 100 && isUploading ? 'Processing…' : 'Upload Progress…'}</div>
                </div>
                <div className="w-full max-w-screen-xl h-1 bg-gray-200 rounded-full opacity-100 mt-2">
                  <div
                    className="bg-blue-500 h-1 rounded-full transition-all duration-300 "
                    style={{ width: `${totalProgress}%` }}
                  >
                    {/* {totalProgress}%  */}
                  </div>
                </div>
              </div>
            )}
          {showUploadPanel == UploadPanels.UploadComplete && (
            <div className="flex flex-col justify-center w-full bg-green-50">
              <div className="flex flex-col items-center justify-center w-full">
                <div className="text-lg font-semibold">All files uploaded successfully!</div>
                <div className="font-normal text-sm text-gray-400">
                  They’ve been added to your Media Kit and are ready to use.
                </div>
                <div className="flex flex-row justify-center items-center w-full p-2 pt-12">
                  <div className="flex flex-row justify-center w-48 h-48 text-green-501">
                    <CircleCheckBigIcon className="w-48 h-48" />
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:space-x-4 pt-8 pb-8 px-4 gap-2-1">
                { !isReplaced && <Button
                  variant="primary"
                  className="flex-1"
                  onClick={() => {
                    handleClearFiles();
                  }}
                >
                  Upload More Media
                </Button> }
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    handleClearFiles();
                    handleCloseUploader();
                  }}
                >
                  {'Close'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomUppyUploaderv2;
